import { config } from '../Environment';
import { RecordingData } from '../hooks/useMediaRecorder';

const API_BASE_URL = config.API_BASE;

interface PresignedUrlResponse {
  uploadUrl: string;
  key: string;
  expiresIn: number;
  maxSize: number;
}

interface SaveRecordingRequest {
  key: string;
  type: 'audio' | 'video';
  context: 'pane' | 'prologue' | 'epilogue';
  activityId?: string;
  duration: number;
  size: number;
  mimeType: string;
}

interface SaveRecordingResponse {
  message: string;
  recording: RecordingData;
}

/**
 * Get presigned URL for uploading recording to R2
 */
export async function getPresignedUploadUrl(
  processId: string,
  type: 'audio' | 'video',
  context: 'pane' | 'prologue' | 'epilogue',
  activityId?: string,
  retryFetch?: (url: string, options?: RequestInit) => Promise<Response>
): Promise<PresignedUrlResponse> {
  const url = `${API_BASE_URL}/api/recordings/presigned-url`;
  const options: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ processId, type, context, activityId }),
  };

  const response = retryFetch
    ? await retryFetch(url, options)
    : await fetch(url, options);

  if (!response.ok) {
    if (response.status === 429) {
      const error = await response.json();
      throw new Error(error.message || 'Rate limit exceeded. Please try again later.');
    }
    const error = await response.json().catch(() => ({ error: 'Failed to get presigned URL' }));
    throw new Error(error.error || 'Failed to get presigned URL');
  }

  return response.json();
}

/**
 * Upload recording blob directly to R2 using presigned URL
 */
export async function uploadRecordingToR2(
  uploadUrl: string,
  blob: Blob,
  mimeType: string
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': mimeType,
    },
    body: blob,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload to R2: ${response.statusText}`);
  }
}

/**
 * Save recording metadata to backend after successful upload
 */
export async function saveRecordingMetadata(
  processId: string,
  data: SaveRecordingRequest,
  retryFetch?: (url: string, options?: RequestInit) => Promise<Response>
): Promise<SaveRecordingResponse> {
  const url = `${API_BASE_URL}/api/recordings/processes/${processId}/recordings`;
  const options: RequestInit = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  };

  const response = retryFetch
    ? await retryFetch(url, options)
    : await fetch(url, options);

  if (!response.ok) {
    if (response.status === 413) {
      const error = await response.json();
      throw new Error(error.error || 'File size exceeds limit');
    }
    const error = await response.json().catch(() => ({ error: 'Failed to save recording metadata' }));
    throw new Error(error.error || 'Failed to save recording metadata');
  }

  return response.json();
}

/**
 * Delete recording from R2 and remove metadata
 */
export async function deleteRecording(
  processId: string,
  type: 'audio' | 'video',
  context: 'pane' | 'prologue' | 'epilogue',
  activityId?: string,
  retryFetch?: (url: string, options?: RequestInit) => Promise<Response>
): Promise<void> {
  const url = `${API_BASE_URL}/api/recordings/processes/${processId}/recordings`;
  const options: RequestInit = {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ type, context, activityId }),
  };

  const response = retryFetch
    ? await retryFetch(url, options)
    : await fetch(url, options);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete recording' }));
    throw new Error(error.error || 'Failed to delete recording');
  }
}

/**
 * Update auto-play state for a process activity
 * Marks that a single-mode auto-play has already been played
 */
export async function updateAutoPlayState(
  processId: string,
  activityId: string,
  type: 'audio' | 'video',
  retryFetch?: (url: string, options?: RequestInit) => Promise<Response>
): Promise<{ message: string; autoPlayState: Record<string, { playedAt: string }> }> {
  const url = `${API_BASE_URL}/api/processes/${processId}/autoplay-state`;
  const options: RequestInit = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ activityId, type }),
  };

  const response = retryFetch
    ? await retryFetch(url, options)
    : await fetch(url, options);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to update auto-play state' }));
    throw new Error(error.error || 'Failed to update auto-play state');
  }

  return response.json();
}
